import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/PromoStrip.css';

const API = process.env.REACT_APP_API_URL || '';

export interface Strip {
  id: number;
  title: string;
  subtitle?: string | null;
  image_url?: string | null;
  emoji?: string | null;
  link_type: 'none' | 'product' | 'campaign' | 'route' | 'url';
  link_ref?: string | null;
  bg_color?: string | null;
  text_color?: string | null;
}

/**
 * The one-line promo row on Home. Content, colours and destination all come from
 * the DB, so a campaign or a price deal can be swapped without a deploy.
 */
const PromoStrip: React.FC = () => {
  const [strips, setStrips] = useState<Strip[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/promo-strips?platform=web`)
      .then(r => r.json())
      .then(json => setStrips(json.success ? json.data : []))
      // Decorative — a failure here must never break the page.
      .catch(() => setStrips([]));
  }, []);

  if (strips.length === 0) return null;

  const go = (s: Strip) => {
    if (s.link_type === 'product' && s.link_ref) navigate(`/vegetable/${s.link_ref}`);
    else if (s.link_type === 'route' && s.link_ref) navigate(s.link_ref);
    else if (s.link_type === 'url' && s.link_ref) window.open(s.link_ref, '_blank', 'noreferrer');
    // 'campaign' has no public page until the winners page ships; treat as static.
  };

  return (
    <div className="promo-strip-list">
      {strips.map(s => {
        const clickable = s.link_type !== 'none' && !!s.link_ref;
        return (
          <div
            key={s.id}
            className={`promo-strip${clickable ? ' promo-strip--clickable' : ''}`}
            style={{
              ...(s.bg_color ? { background: s.bg_color } : {}),
              ...(s.text_color ? { color: s.text_color } : {}),
            }}
            onClick={clickable ? () => go(s) : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter') go(s); } : undefined}
          >
            {s.image_url
              ? <img className="promo-strip-img" src={s.image_url} alt="" loading="lazy" />
              : s.emoji
                ? <span className="promo-strip-emoji">{s.emoji}</span>
                : null}
            <span className="promo-strip-text">
              <span className="promo-strip-title">{s.title}</span>
              {s.subtitle && <span className="promo-strip-sub">{s.subtitle}</span>}
            </span>
            {clickable && <span className="promo-strip-arrow">›</span>}
          </div>
        );
      })}
    </div>
  );
};

export default PromoStrip;
